import { ChangeDetectionStrategy, Component, effect, inject, input, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs/internal/firstValueFrom';

import {
  injectTrialSettingByName,
  injectUpdateTrialSettingMutation,
} from '@models/trial-settings/api/trial-settings.api';
import { DiscontinuationRateMethodology } from '@models/trial-settings/enums/discontinuation-setting.enum';

import { IconComponent } from '@shared/components/icon/icon.component';
import { RadioButtonGroupComponent } from '@shared/components/radio-button/radio-button-group.component';
import { RadioButtonComponent } from '@shared/components/radio-button/radio-button.component';
import { SkeletonLoaderDirective } from '@shared/directives/skeleton-loader.directive';
import { EntityType, EventType, TrialSettingKey } from '@shared/services/gql.service';
import { EventService } from '@shared/store/event/event.service';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RadioButtonGroupComponent,
    RadioButtonComponent,
    ReactiveFormsModule,
    IconComponent,
    SkeletonLoaderDirective,
  ],
  selector: 'aux-discontinuation-rate-radio',
  templateUrl: './discontinuation-rate-radio.component.html',
})
export class DiscontinuationRateRadioComponent {
  private eventService = inject(EventService);

  discontinuationRate = injectTrialSettingByName(
    TrialSettingKey.TRIAL_SETTING_DISCONTINUATION_RATE_METHODOLOGY
  );
  updateTrialSettingMutation = injectUpdateTrialSettingMutation();

  initialDiscontinuationRate = signal<DiscontinuationRateMethodology>(
    DiscontinuationRateMethodology.VISIT_LEVEL
  );

  discontinuationRateRadioFc = new FormControl<DiscontinuationRateMethodology>(
    DiscontinuationRateMethodology.VISIT_LEVEL
  );

  value = toSignal(this.discontinuationRateRadioFc.valueChanges);

  disableTooltip = input<string>('');

  _setDefaultDiscontinuationRate = effect(() => {
    this.setDefaultDiscontinuationRate();
  });
  discontinuationValues = DiscontinuationRateMethodology;

  setDefaultDiscontinuationRate() {
    const defaultValue = (this.discontinuationRate.setting()?.setting_value ??
      DiscontinuationRateMethodology.VISIT_LEVEL) as DiscontinuationRateMethodology;

    this.initialDiscontinuationRate.set(defaultValue);
    this.discontinuationRateRadioFc.setValue(defaultValue);
  }

  async updateDiscontinuedSetting() {
    if (this.discontinuationRateRadioFc.value === this.initialDiscontinuationRate()) return;

    const result = await this.updateTrialSettingMutation.mutateAsync({
      setting_key: TrialSettingKey.TRIAL_SETTING_DISCONTINUATION_RATE_METHODOLOGY,
      setting_value:
        this.discontinuationRateRadioFc.value?.toString() ??
        DiscontinuationRateMethodology.VISIT_LEVEL,
    });

    await firstValueFrom(
      this.eventService.processEvent$({
        entity_id: '',
        entity_type: EntityType.TRIAL,
        type: EventType.UPDATE_INVESTIGATOR_SCHEDULES,
      })
    );

    return result;
  }
}
